import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Toolbar from '@mui/material/Toolbar';
import type { TransitionProps } from '@mui/material/transitions';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { ReactElement, ReactNode, Ref } from 'react';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { s3ObjectPublicUrl } from '../../utils/s3PublicUrl';
import { BOOK_LANGUAGE_FORM_EN_ID } from '../Settings/lists/bookLanguageFormSupport';
import type { AuthorRow, BookLanguageRow } from '../Settings/lists/singleTableListSupport';
import { authorRowSummaryLabel, bookLanguageFlagDisplaySrc } from '../Settings/lists/singleTableListSupport';
import {
  BOOK_COVER_PROMO_IMAGE_HEIGHT_PX,
  BOOK_COVER_PROMO_IMAGE_WIDTH_PX,
  BOOK_PAGE_IMAGE_HEIGHT_PX,
  BOOK_PAGE_IMAGE_WIDTH_PX,
} from './bookForm/constants';
import type { BookFormDraft, BookPageDraft } from './bookForm/types';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

function mediaSrc(file: File | null, storageKey: string, blobUrl: string | null): string | null {
  return blobUrl ?? s3ObjectPublicUrl(storageKey.trim() || null);
}

export type BookPreviewProps = {
  open: boolean;
  onClose: () => void;
  draft: BookFormDraft;
  authors: AuthorRow[];
  languages: BookLanguageRow[];
};

function sortedPagesDraft(pages: BookPageDraft[]): BookPageDraft[] {
  return [...pages].sort((a, b) => a.page_number - b.page_number);
}

/** Next page enters from the right (MUI `left`); previous from the left (`right`). */
function PreviewPageTransition({
  pageKey,
  direction,
  animateEnter,
  reducedMotion,
  children,
}: {
  pageKey: number;
  direction: 'left' | 'right';
  animateEnter: boolean;
  reducedMotion: boolean;
  children: ReactNode;
}) {
  const slideMs = reducedMotion ? 0 : 320;
  const fadeMs = reducedMotion ? 0 : 180;
  return (
    <Box key={pageKey} sx={{ width: 1, overflow: 'hidden' }}>
      <Slide appear={animateEnter} direction={direction} in timeout={slideMs}>
        <Box sx={{ width: 1 }}>
          <Fade appear={animateEnter} in timeout={fadeMs}>
            <Box sx={{ width: 1 }}>{children}</Box>
          </Fade>
        </Box>
      </Slide>
    </Box>
  );
}

export default function BookPreview({ open, onClose, draft, authors, languages }: BookPreviewProps) {
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const coverBlob = useObjectUrl(draft.cover_image_file);
  const promoBlob = useObjectUrl(draft.promo_image_file);
  const enabledLangRows = useMemo(
    () => languages.filter((l) => draft.language_enabled[l.id]),
    [languages, draft.language_enabled],
  );
  const enabledLangIds = useMemo(() => enabledLangRows.map((l) => l.id), [enabledLangRows]);

  const [previewLangId, setPreviewLangId] = useState<string>(BOOK_LANGUAGE_FORM_EN_ID);
  const [slideIndex, setSlideIndex] = useState(0);
  const [slideEnterDirection, setSlideEnterDirection] = useState<'left' | 'right'>('left');
  const [transitionGeneration, setTransitionGeneration] = useState(0);

  useEffect(() => {
    if (!open) return;
    const preferred =
      enabledLangIds.includes(BOOK_LANGUAGE_FORM_EN_ID) ? BOOK_LANGUAGE_FORM_EN_ID : enabledLangIds[0];
    if (preferred && !enabledLangIds.includes(previewLangId)) {
      setPreviewLangId(preferred);
    }
  }, [open, enabledLangIds, previewLangId]);

  useEffect(() => {
    if (open) {
      setSlideIndex(0);
      setTransitionGeneration(0);
    }
  }, [open]);

  const pages = useMemo(() => sortedPagesDraft(draft.pages), [draft.pages]);
  const totalSlides = 1 + pages.length;
  const isCover = slideIndex === 0;
  const pageForSlide = !isCover ? pages[slideIndex - 1] ?? null : null;

  const authorLabel = useMemo(() => {
    const row = authors.find((a) => a.id === draft.author_id);
    return row ? authorRowSummaryLabel(row) : '';
  }, [authors, draft.author_id]);

  const title = (draft.name_map[previewLangId] ?? '').trim() || '…';
  const description = (draft.description_map[previewLangId] ?? '').trim();
  const pageText = pageForSlide
    ? (pageForSlide.byLang[previewLangId]?.full_text ?? '').trim()
    : '';

  const goPrev = () => {
    setSlideEnterDirection('right');
    setTransitionGeneration((g) => g + 1);
    setSlideIndex((i) => Math.max(0, i - 1));
  };

  const goNext = () => {
    setSlideEnterDirection('left');
    setTransitionGeneration((g) => g + 1);
    setSlideIndex((i) => Math.min(totalSlides - 1, i + 1));
  };

  const goToSlide = (i: number) => {
    if (i === slideIndex) return;
    setSlideEnterDirection(i > slideIndex ? 'left' : 'right');
    setTransitionGeneration((g) => g + 1);
    setSlideIndex(i);
  };

  const coverSrc = mediaSrc(draft.cover_image_file, draft.cover_image_path, coverBlob);
  const promoSrc = mediaSrc(draft.promo_image_file, draft.promo_image_path, promoBlob);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      aria-labelledby="book-preview-title"
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: 'background.default',
          backgroundImage: (t) =>
            `linear-gradient(160deg, ${t.palette.primary.light}14 0%, transparent 45%, ${t.palette.secondary.light}12 100%)`,
        },
      }}
    >
      <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar variant="dense" sx={{ gap: 1 }}>
          <MenuBookOutlined color="primary" sx={{ opacity: 0.9 }} />
          <Typography id="book-preview-title" variant="subtitle1" component="div" sx={{ flex: 1, fontWeight: 600 }}>
            Урьдчилан харах
          </Typography>
          {enabledLangRows.length > 1 ? (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={previewLangId}
              onChange={(_, v: string | null) => v && setPreviewLangId(v)}
              aria-label="Хэл сонгох"
              sx={{ flexShrink: 0 }}
            >
              {enabledLangRows.map((l) => {
                const flagSrc = bookLanguageFlagDisplaySrc(l);
                return (
                  <ToggleButton key={l.id} value={l.id} sx={{ px: 1, py: 0.5, minWidth: 0 }}>
                    {flagSrc ? (
                      <Box
                        component="img"
                        src={flagSrc}
                        alt=""
                        sx={{ width: 22, height: 16, objectFit: 'cover', borderRadius: 0.5 }}
                      />
                    ) : (
                      <Typography variant="caption" fontWeight={600}>
                        {l.id.toUpperCase()}
                      </Typography>
                    )}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          ) : null}
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="Хаах">
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 3 },
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        {enabledLangRows.length === 0 ? (
          <Typography color="text.secondary" textAlign="center">
            Урьдчилан харахын тулд дор хаяж нэг хэл идэвхжүүлнэ үү.
          </Typography>
        ) : (
          <PreviewPageTransition
            pageKey={slideIndex}
            direction={slideEnterDirection}
            animateEnter={transitionGeneration > 0}
            reducedMotion={prefersReducedMotion}
          >
            {isCover ? (
              <CoverSlide
                coverSrc={coverSrc}
                promoSrc={promoSrc}
                title={title}
                authorLabel={authorLabel}
                description={description}
              />
            ) : pageForSlide ? (
              <StoryPageSlide page={pageForSlide} text={pageText} previewLangId={previewLangId} />
            ) : null}
          </PreviewPageTransition>
        )}
      </Box>

      {enabledLangRows.length > 0 ? (
        <Box
          component="footer"
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.surfaceLow',
            px: 2,
            py: 1.5,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <IconButton color="primary" onClick={goPrev} disabled={slideIndex <= 0} aria-label="Өмнөх хуудас">
              <ChevronLeft fontSize="large" />
            </IconButton>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ flex: 1 }}>
              {isCover ? 'Нүүр' : `Хуудас ${pageForSlide?.page_number ?? ''} / ${pages.length}`}
            </Typography>
            <IconButton
              color="primary"
              onClick={goNext}
              disabled={slideIndex >= totalSlides - 1}
              aria-label="Дараагийн хуудас"
            >
              <ChevronRight fontSize="large" />
            </IconButton>
          </Stack>
          <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            {Array.from({ length: totalSlides }, (_, i) => (
              <Box
                key={i}
                onClick={() => goToSlide(i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goToSlide(i);
                  }
                }}
                sx={{
                  width: i === 0 || i === totalSlides - 1 ? 8 : 10,
                  height: 8,
                  borderRadius: 4,
                  cursor: 'pointer',
                  bgcolor: i === slideIndex ? 'primary.main' : 'action.disabledBackground',
                  transition: theme.transitions.create('transform', { duration: 150 }),
                  transform: i === slideIndex ? 'scale(1.15)' : 'none',
                }}
              />
            ))}
          </Stack>
        </Box>
      ) : null}
    </Dialog>
  );
}

function CoverSlide({
  coverSrc,
  promoSrc,
  title,
  authorLabel,
  description,
}: {
  coverSrc: string | null;
  promoSrc: string | null;
  title: string;
  authorLabel: string;
  description: string;
}) {
  const w = BOOK_COVER_PROMO_IMAGE_WIDTH_PX;
  const h = BOOK_COVER_PROMO_IMAGE_HEIGHT_PX;
  const coverAspect = `${w} / ${h}`;
  const heroSlotWidth = `min(100%, ${w}px, calc(62dvh * ${w} / ${h}))`;
  const heroSlotMaxHeight = `min(62dvh, calc((100dvw - 48px) * ${h} / ${w}))`;

  const hasCover = Boolean(coverSrc);
  const hasPromo = Boolean(promoSrc);
  const showCoverPromoToggle = hasCover && hasPromo;
  const [heroKind, setHeroKind] = useState<'cover' | 'promo'>('cover');

  useEffect(() => {
    if (hasCover && hasPromo) {
      setHeroKind('cover');
    }
  }, [hasCover, hasPromo, coverSrc, promoSrc]);

  const frameSx = {
    width: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 3,
    overflow: 'hidden',
    boxShadow: 6,
    bgcolor: 'background.surfaceHigh',
    border: 1,
    borderColor: 'divider',
  } as const;

  /** Fixed 1200:1520 frame; image fills via centered `object-fit: cover` (matches app crop). */
  const coverPromoAspectSlot = (slotWidth: string, slotMaxHeight: string, inner: ReactNode) => (
    <Box sx={frameSx}>
      <Box
        sx={{
          width: slotWidth,
          maxHeight: slotMaxHeight,
          aspectRatio: coverAspect,
          position: 'relative',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          overflow: 'hidden',
        }}
      >
        <Box
          aria-hidden
          sx={(t) => {
            const edge = alpha(t.palette.common.black, t.palette.mode === 'dark' ? 0.55 : 0.14);
            const midHi = alpha(t.palette.common.white, t.palette.mode === 'dark' ? 0.12 : 0.42);
            const groove = alpha(t.palette.common.black, t.palette.mode === 'dark' ? 0.35 : 0.1);
            const spine = alpha(t.palette.text.primary, t.palette.mode === 'dark' ? 0.12 : 0.06);
            return {
              flexShrink: 0,
              width: { xs: 12, sm: 16 },
              minHeight: 0,
              bgcolor: t.palette.mode === 'dark' ? alpha(t.palette.grey[900], 0.45) : alpha(t.palette.grey[400], 0.35),
              backgroundImage: `linear-gradient(90deg,
                ${edge} 0%,
                ${groove} 14%,
                ${midHi} 46%,
                ${groove} 58%,
                ${spine} 78%,
                ${alpha(t.palette.divider, 0.9)} 100%)`,
              boxShadow: `inset -4px 0 10px ${alpha(t.palette.common.black, t.palette.mode === 'dark' ? 0.5 : 0.12)}`,
              borderRight: `1px solid ${alpha(t.palette.common.black, t.palette.mode === 'dark' ? 0.35 : 0.08)}`,
            };
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>{inner}</Box>
      </Box>
    </Box>
  );

  const heroSrc =
    showCoverPromoToggle && heroKind === 'promo' ? promoSrc : coverSrc ?? promoSrc;

  return (
    <Stack
      alignItems="center"
      spacing={3}
      sx={{ width: 1, maxWidth: 'min(960px, 100%)', textAlign: 'center' }}
    >
      {showCoverPromoToggle ? (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={heroKind}
          onChange={(_, v: 'cover' | 'promo' | null) => v != null && setHeroKind(v)}
          aria-label="Нүүр эсвэл промо зураг"
        >
          <ToggleButton value="cover">Нүүр зураг</ToggleButton>
          <ToggleButton value="promo">Промо зураг</ToggleButton>
        </ToggleButtonGroup>
      ) : null}
      {heroSrc
        ? coverPromoAspectSlot(
            heroSlotWidth,
            heroSlotMaxHeight,
            <Box
              component="img"
              src={heroSrc}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
                borderTopRightRadius: 10,
                borderBottomRightRadius: 10,
              }}
            />,
          )
        : coverPromoAspectSlot(
            `min(100%, 320px, calc(48dvh * ${w} / ${h}))`,
            `min(48dvh, calc((100dvw - 48px) * ${h} / ${w}))`,
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
              }}
            >
              <MenuBookOutlined sx={{ fontSize: 80, opacity: 0.5 }} />
            </Box>,
          )}
      <Box>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        {authorLabel ? (
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
            {authorLabel}
          </Typography>
        ) : null}
      </Box>
      {description ? (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            lineHeight: 1.7,
            maxWidth: 440,
            whiteSpace: 'pre-wrap',
          }}
        >
          {description}
        </Typography>
      ) : null}
    </Stack>
  );
}

function StoryPageSlide({
  page,
  text,
  previewLangId,
}: {
  page: BookPageDraft;
  text: string;
  previewLangId: string;
}) {
  const pw = BOOK_PAGE_IMAGE_WIDTH_PX;
  const ph = BOOK_PAGE_IMAGE_HEIGHT_PX;
  const pageAspect = `${pw} / ${ph}`;
  const pageSlotWidth = `min(100%, ${pw}px, calc(min(85dvh, calc(100dvw - 32px)) * ${pw} / ${ph}))`;
  const pageSlotMaxHeight = `min(85dvh, calc((100dvw - 32px) * ${ph} / ${pw}))`;

  const pageBlob = useObjectUrl(page.image_file);
  const imgSrc = mediaSrc(page.image_file, page.image_path, pageBlob);
  const videoSrc = s3ObjectPublicUrl(page.video_path.trim() || null);

  return (
    <Stack
      spacing={2}
      sx={{
        width: 1,
        maxWidth: 'min(960px, 100%)',
        alignItems: 'stretch',
      }}
    >
      <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
        Зургийн зориулалт: {`${pw}×${ph}`} пиксел (өргөн {pw}). Доорх урьдчилан харах нь апп дээрх шиг{' '}
        {pw}:{ph} цонхонд төвөөс таслагдан харагдана.
      </Typography>
      <Box
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'background.surfaceHigh',
          border: 1,
          borderColor: 'divider',
          boxShadow: 2,
        }}
      >
        {imgSrc ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'background.default',
              p: 0.5,
            }}
          >
            <Box
              sx={{
                width: 1,
                display: 'flex',
                justifyContent: 'center',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: pageSlotWidth,
                  maxHeight: pageSlotMaxHeight,
                  aspectRatio: pageAspect,
                  position: 'relative',
                  bgcolor: 'background.default',
                }}
              >
                <Box
                  component="img"
                  src={imgSrc}
                  alt=""
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    display: 'block',
                  }}
                />
              </Box>
            </Box>
          </Box>
        ) : videoSrc ? null : (
          <Box
            sx={{
              minHeight: { xs: 200, sm: 240 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.disabled',
              bgcolor: 'background.default',
            }}
          >
            <Typography variant="body2">Зураг эсвэл видео оруулна уу</Typography>
          </Box>
        )}
        {videoSrc ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'background.default', p: 0.5 }}>
            <Box
              component="video"
              src={videoSrc}
              controls
              playsInline
              sx={{
                width: 'auto',
                maxWidth: '100%',
                maxHeight: 'min(85dvh, calc(100dvw - 32px))',
                display: 'block',
                bgcolor: 'background.default',
              }}
            />
          </Box>
        ) : null}
      </Box>
      <PaperCard>
        <Typography
          variant="body1"
          component="div"
          sx={{
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
            lineHeight: 1.75,
            letterSpacing: '0.01em',
            whiteSpace: 'pre-wrap',
            color: 'text.primary',
            fontFamily: (t) => t.typography.body1.fontFamily,
          }}
        >
          {text || (
            <Typography component="span" color="text.disabled" fontStyle="italic">
              Энэ хуудсанд «{previewLangId.toUpperCase()}» хэлээр текст байхгүй байна.
            </Typography>
          )}
        </Typography>
      </PaperCard>
    </Stack>
  );
}

function PaperCard({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        boxShadow: (t) => (t.palette.mode === 'dark' ? t.shadows[3] : t.shadows[2]),
      }}
    >
      {children}
    </Box>
  );
}
